import React from 'react';
import classnames from 'classnames';
import { isEmpty, get } from 'lodash';

// local dependencies
import { WIDGET_ERROR_TYPE, GLOBAL_LABELS, WIDGET_ERROR_TEMPLATE } from 'constants/common';
import { Icon } from 'modules/icon-bank';
import { getLabel } from 'utils/getLabel';

// card type icons
const WIDGET_ERROR = {
    [ WIDGET_ERROR_TEMPLATE.DEFAULT ]: 'somethingWentWrong',
    [ WIDGET_ERROR_TEMPLATE.WARNING ]: 'warningBlue'
};

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { widgetError as WidgetError } from 'ui/views/widgets/widgetError';
 *
 * // render
 * <WidgetError propName='propValue'/>
 */
export const widgetError = ( props ) => {

    // default error type
    if( WIDGET_ERROR_TYPE.DEFAULT === props.type ) {

        // dynamic widget class name
        const widgetClassName = classnames( 'ui-widget-error-default', props.className, {
            [ `ui-widget-error-default--size-${ props.size }` ]: ! isEmpty( props.size ),
            [ `ui-widget-error-default--padding-${ props.errorViewConfig.padding }` ]: ! isEmpty( props.errorViewConfig.padding ),
        } );

        // icon sizes
        const ICON_SIZE = {
            default: [ 243, 135 ],
            xs: [ 50, 50 ],
            small: [ 120, 80 ],
            medium: [ 288, 160 ],
            large: [ 360, 200 ],
        };

        // get title and subtitle
        const title = isEmpty( get( props.networkError, 'data.title' ) ) ? getLabel( GLOBAL_LABELS.DISPLAY_TEXT_SOMETHING_WENT_WRONG ) : get( props.networkError, 'data.title' );
        const subtitle = isEmpty( get( props.networkError, 'data.description' ) ) ? getLabel( GLOBAL_LABELS.DISPLAY_TEXT_TRY_AGAIN_LATER ) : get( props.networkError, 'data.description' );
        const template = get( props.networkError, 'data.template', WIDGET_ERROR_TEMPLATE.DEFAULT );
        const iconSize = WIDGET_ERROR_TEMPLATE.DEFAULT === template ? props.size: 'xs';

        const iconClass = classnames( 'ui-widget-error-default__icon',
            `ui-widget-error-default__icon--${template}`
        );

        return (
            <div className={ widgetClassName }>

                {/* icon */}
                <div className={ iconClass }>
                    
                    <Icon name={ WIDGET_ERROR[ `${template}` ] } width={ ICON_SIZE[ iconSize ][ 0 ] } height={ ICON_SIZE[ iconSize ][ 1 ] } />
                </div>

                {/* title */}
                <p className='ui-widget-error-default__title'>{ title }</p>

                {/* sub-title */}
                <p className='ui-widget-error-default__sub-title'>{ subtitle }</p>
            </div>
        );
    }

    /*---------*/

    // no-data-found error type
    if( WIDGET_ERROR_TYPE.NO_DATA_FOUND === props.type ) {

        // dynamic widget class name
        const widgetClassName = classnames( 'ui-widget-error-no-data-found', props.className, {
            [ `ui-widget-error-no-data-found--size-${ props.size }` ]: ! isEmpty( props.size ),
        } );

        // icon sizes
        const ICON_SIZE = {
            default: [ 243, 137 ],
            medium: [ 288, 161 ],
            large: [ 360, 202 ],
        };

        return (
            <div className={ widgetClassName }>

                {/* icon */}
                <div className='ui-widget-error-no-data-found__icon'>
                    <Icon name='noDataFound' width={ ICON_SIZE[ props.size ][ 0 ] } height={ ICON_SIZE[ props.size ][ 1 ] } />
                </div>

                {/* sub-title */}
                <p className='ui-widget-error-no-data-found__sub-title'>
                    { isEmpty( props.label ) ? getLabel( GLOBAL_LABELS.DISPLAY_TEXT_NO_DATA_FOUND ) : getLabel( props.label ) }
                </p>
            </div>
        );
    }

    /*---------*/

    // no-match-found error type
    if( WIDGET_ERROR_TYPE.NO_MATCH_FOUND === props.type ) {

        // dynamic widget class name
        const widgetClassName = classnames( 'ui-widget-error-no-match-found', props.className, {
            [ `ui-widget-error-no-match-found--size-${ props.size }` ]: ! isEmpty( props.size ),
        } );

        // icon sizes
        const ICON_SIZE = {
            default: [ 243, 137 ],
            medium: [ 288, 161 ],
            large: [ 360, 202 ],
        };

        return (
            <div className={ widgetClassName }>

                {/* icon */}
                <div className='ui-widget-error-no-match-found__icon'>
                    <Icon name='noDataFound' width={ ICON_SIZE[ props.size ][ 0 ] } height={ ICON_SIZE[ props.size ][ 1 ] } />
                </div>

                {/* sub-title */}
                <p className='ui-widget-error-no-match-found__sub-title'>
                    { getLabel( GLOBAL_LABELS.DISPLAY_TEXT_NO_MATCH_FOUND ) }
                </p>
            </div>
        );
    }
};

// set display name
widgetError.displayName = 'widgetError';

// set default props
widgetError.defaultProps = {
    type: WIDGET_ERROR_TYPE.DEFAULT,
    size: 'default',
    errorViewConfig: {},
    networkError: {},
};

// prop types
widgetError.propTypes = {};
